<template>
  <section id="ai-inspect-wrapper" class="verified-inspect" :class="{ 'theme-dark': isDark }">
    <div class="inspect-layout" :class="{ 'inspect-layout--viewer': viewerOpened }">
      <verified-anomalies-table
        :entries="entries" :groups="groups" :expanded-group-ids="expandedGroupIds" :group-status="groupStatus"
        :images="images" :total="total" :loading="loading || queryPending" :error="listError"
        :options="options" :selected-ids="selectedIds" :active-id="activeId"
        :density="density" :filters-applied="filtersApplied" :busy="busy || loading || positionInteractionLocked"
        @update:selected-ids="selectedIds = $event" @update:options="setOptions" @update:density="setDensity"
        @open="openAnomaly" @report="openInspectReport" @isolate="isolateIn3D"
        @link="openComponentPicker" @unlink="unlinkComponent"
        @corrosion="openCorrosionAnalyser" @delete="deleteInspect" @retry="refresh" @clear-filters="clearFilters" @open-analysis="openAnalysis"
        @toggle-group="toggleGroup" @retry-group="retryGroup">
        <template #header-start>
          <v-text-field :value="options.search" label="Search verified anomalies" prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable class="inspect-search" :disabled="positionInteractionLocked" @input="search" />
        </template>
        <template #header-actions>
          <v-btn icon class="inspect-toolbar__control" :disabled="loading || positionInteractionLocked" aria-label="Refresh verified anomalies" title="Refresh" @click="refresh"><v-icon>mdi-refresh</v-icon></v-btn>
          <v-btn outlined class="inspect-toolbar__control inspect-filter-button" :disabled="positionInteractionLocked" @click="$refs.filters.open()"><v-icon small left>mdi-filter-outline</v-icon>Filters</v-btn>
        </template>
        <template #filter-context>
          <div v-if="filterChips.length" class="inspect-filters" aria-label="Active filters">
            <v-chip v-for="chip in filterChips" :key="chip.key + ':' + chip.value" small close @click:close="removeFilter(chip)">{{ chip.label }}: {{ chip.value }}</v-chip>
            <v-btn small text @click="clearFilters">Clear filters</v-btn>
          </div>
        </template>
        <template #bulk-actions>
          <span :title="planDisabledReason"><v-btn small depressed color="primary" :disabled="!!planDisabledReason || busy || loading || positionInteractionLocked" @click="addToPlan"><v-icon small left>mdi-calendar-plus</v-icon>Add to plan</v-btn></span>
          <v-btn small outlined :disabled="busy || loading || positionInteractionLocked || !selectedRows.length" @click="assessCondition">Condition Assessment</v-btn>
          <span title="Show selected anomalies in their 3D context"><v-btn small outlined :disabled="busy || loading || positionInteractionLocked || !canIsolateSelection" @click="isolateSelected"><v-icon small left>mdi-cube-outline</v-icon>Isolate</v-btn></span>
          <v-btn small text color="error" :disabled="busy || loading || positionInteractionLocked" @click="deleteSelected">Delete</v-btn>
        </template>
        <template v-if="activeFinding" #panel>
          <div class="inspect-details">
            <v-progress-linear v-if="detailLoading" indeterminate height="2" />
            <v-alert v-if="detailError" dense text type="warning" role="alert">{{ detailError }} <v-btn small text @click="openAnomaly(activeFinding)">Try again</v-btn></v-alert>
            <verified-anomaly-detail-panel :finding="activeFinding" :scan-name="scanName(activeFinding.imageId)" :busy="busy || detailLoading || !!detailError || loading || positionInteractionLocked"
              @close="closeDetails" @save="saveDetails" @link="openComponentPicker" @unlink="unlinkComponent" @report="openInspectReport(activeFinding)" @isolate="isolateIn3D(activeFinding)" @corrosion="openCorrosionAnalyser(activeFinding)" @source-report="openSourceReport(activeFinding)" />
          </div>
        </template>
      </verified-anomalies-table>
      <section v-if="viewerOpened" class="inspect-viewer" :class="{ 'inspect-viewer--expanded': viewerExpanded }" aria-label="Anomaly spatial context">
        <viewers-section
          ref="viewersSection"
          :viewer-tab.sync="viewerTab"
          :inspects="viewerInspects"
          :enabled-tabs="viewerTabs"
          :interaction-locked="positionInteractionLocked"
          feature="ai-inspect-verified"
          @inspect-selected="onPolygonSelected"
          @expand="viewerExpanded = !viewerExpanded"
          @closeViewersSection="closeViewer"
          @panorama-ready="focusCurrentPolygon">
          <template #viewer-overlay="{ activeTab }">
            <v-tooltip v-if="activeTab === '360 View' && imagesError" bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="inspect-viewer__image-retry"
                  type="button"
                  fab
                  small
                  color="warning"
                  aria-label="Retry loading scan images"
                  @click="loadImages">
                  <v-icon>mdi-image-refresh-outline</v-icon>
                </v-btn>
              </template>
              <span>Retry loading scan images</span>
            </v-tooltip>
            <v-tooltip
              v-if="activeTab === '360 View' && activeFinding && isPrimaryFindingScene"
              bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  ref="resolvePositionButton"
                  class="finding-position-action"
                  type="button"
                  fab
                  small
                  :color="positionResolveColor"
                  :loading="positionFlow.status === 'resolving'"
                  :disabled="!canResolvePosition"
                  :aria-label="positionResolveLabel"
                  @click="resolveActiveFindingPosition">
                  <v-icon>mdi-crosshairs-gps</v-icon>
                </v-btn>
              </template>
              <span>{{ positionResolveLabel }}</span>
            </v-tooltip>
            <v-tooltip
              v-else-if="activeTab === '360 View' && activeFinding && !isPrimaryFindingScene"
              bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="finding-position-action"
                  type="button"
                  fab
                  small
                  outlined
                  color="primary"
                  :disabled="positionInteractionLocked"
                  aria-label="Return to finding scan"
                  @click="showScene(activeFinding)">
                  <v-icon>mdi-arrow-u-left-top</v-icon>
                </v-btn>
              </template>
              <span>Return to finding scan</span>
            </v-tooltip>
            <div
              v-if="activeTab === '3D View' && positionFlow.status === 'resolving'"
              class="finding-position-resolving-cover"
              role="status"
              aria-live="polite">
              <img
                v-if="positionEvidenceImage"
                class="finding-position-resolving-cover__image"
                :src="positionEvidenceImage"
                alt="" />
              <div class="finding-position-resolving-cover__content">
                <v-progress-circular indeterminate size="22" width="3" color="warning" />
                <span>Preparing point-cloud position…</span>
              </div>
            </div>
            <div
              v-if="positionFlow.status === 'preview'"
              ref="positionPreview"
              class="finding-position-banner"
              :class="{ 'finding-position-banner--above-navbar': activeTab === '360 View' }"
              role="region"
              aria-label="3D position preview"
              tabindex="-1">
              <v-icon class="finding-position-banner__icon" color="secondary" size="20">mdi-crosshairs-gps</v-icon>
              <p class="finding-position-banner__message">Preview the candidate point before saving.</p>
              <div class="finding-position-banner__actions">
                <v-btn small depressed class="finding-position-banner__btn--primary" @click="confirmFindingPosition">Confirm & Save</v-btn>
                <v-btn small depressed class="finding-position-banner__btn--outline" @click="retryFindingPosition">Retry</v-btn>
                <v-btn small text class="finding-position-banner__btn--text" @click="cancelFindingPosition">Cancel</v-btn>
              </div>
            </div>
            <div
              v-else-if="positionFlow.status === 'saving'"
              class="finding-position-banner"
              :class="{ 'finding-position-banner--above-navbar': activeTab === '360 View' }"
              role="status"
              aria-live="polite">
              <v-progress-circular class="finding-position-banner__icon" indeterminate size="18" width="2" color="secondary" />
              <p class="finding-position-banner__message">Saving 3D position… Keep this finding open.</p>
            </div>
            <div
              v-else-if="positionFlow.status === 'error'"
              class="finding-position-banner"
              :class="{ 'finding-position-banner--above-navbar': activeTab === '360 View' }"
              role="alert">
              <v-icon class="finding-position-banner__icon" color="warning" size="20">mdi-alert-outline</v-icon>
              <p class="finding-position-banner__message">{{ positionFlow.message }}</p>
              <div class="finding-position-banner__actions">
                <v-btn ref="retryPositionButton" small depressed class="finding-position-banner__btn--primary" @click="retryFindingPosition">Retry</v-btn>
                <v-btn small text class="finding-position-banner__btn--text" @click="cancelFindingPosition">Cancel</v-btn>
              </div>
            </div>
          </template>
        </viewers-section>
        <div v-if="viewerTab === '360 View' && !hasPanoramaEvidence" class="inspect-viewer__empty" role="status"><div><v-icon size="36">mdi-image-off-outline</v-icon><p>360 evidence is unavailable for this observation.</p><p>The scan image and valid detection coordinates are required.</p></div></div>
        <v-alert v-if="viewerTab === '360 View' && fallbackVisible" class="inspect-viewer__fallback" dense text type="info">Outline unavailable — showing the detection bounding box.</v-alert>
        <div v-if="availableScenes.length > 1 && viewerTab === '360 View'" class="inspect-scenes" aria-label="Verified related observations">
          <button v-for="scene in availableScenes" :key="scene.id" type="button" :class="{ 'is-current': viewerImageId === scene.imageId }" :disabled="positionInteractionLocked" @click="showScene(scene)"><img v-if="scene.tiledImageUriPath" :src="scene.tiledImageUriPath" alt="" /><span>{{ scanName(scene.imageId) || 'Related observation' }}</span></button>
        </div>
      </section>
    </div>
    <InsightsFilters ref="filters" />
    <component-picker-dialog
      v-model="componentPicker.open"
      :findings="pickerFinding ? [pickerFinding] : []"
      :facility-id="facilityId"
      :recent="recentComponents"
      :busy="linkingComponent"
      @link="linkComponent"
      @unlink="unlinkPickerComponent" />
    <AddInspectToPlan ref="addToPlan" />
    <InspectReportPopup ref="inspectReport" />
    <ConditionAssessmentConfirmModal ref="conditionAssessmentModal" />
  </section>
</template>

<script>
  import { mapGetters } from 'vuex';
  import InsightsFilters from '../components/InsightsFilters.vue';
  import AddInspectToPlan from '../components/AddInspectToPlan.vue';
  import InspectReportPopup from '../components/InspectReportPopup.vue';
  import ConditionAssessmentConfirmModal from '../components/ConditionAssessmentConfirmModal.vue';
  import ViewersSection from '../components/ViewersSection.vue';
  import VerifiedAnomaliesTable from '../components/aiInspect/VerifiedAnomaliesTable.vue';
  import VerifiedAnomalyDetailPanel from '../components/aiInspect/VerifiedAnomalyDetailPanel.vue';
  import ComponentPickerDialog from '../components/aiAnalysis/ComponentPickerDialog.vue';
  import aIInspectService from '../service/aIInspect.service';
  import coatingAnalyzerService from '../service/coatingAnalyzer.service';
  import equipmentService from '@Tags/services/equipment.service';
  import pipelineService from '@Tags/services/pipeline.service';
  import structuralService from '@Tags/services/structural.service';
  import { componentLinkErrorMessage, componentOfFinding, componentPatch, componentTypeOf, pushRecent } from '../utils/findingComponents';
  import { ForgeInspection } from '@/scripts/js/forgeModels';
  import { getFilterOptionDisplayName } from '../config/helper';
  import { readDensity, storeDensity } from '../components/aiAnalysis/densityPreference';
  import { APPROVAL, parseMaskPoints } from '../utils/findingsMetrics';
  import { buildVerifiedViewerInspects, hasSpatialPoint, createAnomalyDetailLoader } from '../utils/verifiedAnomalyViewer';
  import { acquireFindingPointCloud } from '../utils/findingPointCloudLease';
  import { acquireFindingModelVisibility } from '../utils/findingModelVisibilityLease';
  import { resolveFindingPosition } from '../utils/findingPositionResolver';
  import { showFindingPositionPreview } from '../utils/findingPositionPreview';
  import { pixelToYawPitch } from '../utils/panorama';
  import { error, warn } from '@/utils/debugLogger';
  import { viewerCubeAround } from '@/utils/coordinateAuthority';
  const THREE = window.THREE;
  const DENSITY_KEY = 'ava-inspect-density';
  const CORROSION_CROP_HALF_SIZE_M = 0.5;
  const TAG_SERVICES = { Equipment: equipmentService, Pipeline: pipelineService, Structural: structuralService };
  const idlePositionFlow = revision => ({
    status: 'idle', findingId: null, candidate: null,
    errorCode: null, message: null, revision,
  });
  const mapPositionSaveError = (flow, err) => {
    const status = err?.response?.status;
    const code = err?.response?.data?.code || err?.code || 'POSITION_SAVE_FAILED';
    const messages = {
      ANOMALY_INSIGHT_NOT_ACCEPTED: 'This finding is no longer accepted. Refresh the table.',
      ANOMALY_INSIGHT_IMAGE_CHANGED: 'The finding source scan changed. Refresh and resolve again.',
      ANOMALY_INSIGHT_POSITION_CONFLICT: 'The finding changed while the position was being reviewed. Refresh and resolve again.',
      ANOMALY_INSIGHT_POSITION_INVALID: 'The candidate position is no longer valid. Retry the resolution.',
    };
    const fallback = status === 403
      ? 'You do not have permission to save this finding position.'
      : status === 404
        ? 'This finding is no longer available in this facility.'
        : 'The 3D position could not be saved. Retry the resolution.';
    return { ...flow, status: 'error', errorCode: code, message: messages[code] || fallback };
  };

  export default {
    name: 'AIInspectDashboard',
    components: { InsightsFilters, AddInspectToPlan, InspectReportPopup, ConditionAssessmentConfirmModal, ViewersSection, VerifiedAnomaliesTable, VerifiedAnomalyDetailPanel, ComponentPickerDialog },
    data() {
      return {
        selectedIds: [], activeId: null, activeDetail: null, detailLoading: false, detailError: null,
        viewerOpened: false, viewerExpanded: false, viewerTab: '360 View', viewerImageId: null,
        isolatedInspects: [], spatialError: null, busy: false, pageActive: false,
        scopeVersion: 0, viewerVersion: 0,
        density: readDensity(DENSITY_KEY), requestTimer: null, queryPending: false, querySignature: null,
        componentPicker: { open: false, findingId: null }, linkingComponent: false,
        recentComponents: [], recentComponentsFacilityId: null,
        viewerTabs: ['360 View', '3D View', '2D View'],
        positionFlow: idlePositionFlow(0),
      };
    },
    computed: {
      ...mapGetters('AI_INSPECT', { entries: 'GET_ENTRIES', total: 'GET_TOTAL', loading: 'GET_LOADING', listError: 'LAST_ERROR', images: 'IMAGES', imagesError: 'IMAGES_ERROR', options: 'GET_OPTIONS', groups: 'GROUPS', expandedGroupIds: 'EXPANDED_GROUP_IDS', groupStatus: 'GROUP_STATUS' }),
      facilityId() { return this.$route.params.id; },
      isDark() { return this.$vuetify.theme.dark; },
      forgeService() { return this.$store.state.forgeService; },
      viewer() { return this.$store.state.viewer; },
      facility() { return this.$store.getters['FACILITY/GET_FACILITY']; },
      selectedRows() { const ids = new Set(this.selectedIds); return this.entries.filter(row => ids.has(row.id) && row.approvalStatus === APPROVAL.ACCEPTED); },
      activeFinding() { return this.activeDetail && this.activeDetail.id === this.activeId ? this.activeDetail : this.entries.find(row => row.id === this.activeId) || null; },
      availableScenes() { return this.activeFinding ? [this.activeFinding, ...(this.activeFinding.children || [])].filter(row => row.approvalStatus === APPROVAL.ACCEPTED) : []; },
      polygonInspects() { return buildVerifiedViewerInspects(this.entries, this.activeDetail, this.viewerImageId, this.activeId); },
      viewerInspects() { return this.viewerTab === '360 View' ? this.polygonInspects : this.isolatedInspects; },
      hasPanoramaEvidence() { return this.polygonInspects.some(row => row.ownerId === this.activeId) && this.images.some(image => image.id === this.viewerImageId && (image.imageUrl || image.lowResUrl || image.tilesBaseUrl)); },
      isPrimaryFindingScene() { return !!this.activeFinding && this.viewerImageId === this.activeFinding.imageId; },
      hasActivePosition() { return hasSpatialPoint(this.activeFinding?.point3d); },
      positionEvidenceImage() {
        const image = this.images.find(entry => entry.id === this.activeFinding?.imageId);
        return this.activeFinding?.tiledImageUriPath || image?.lowResUrl || image?.imageUrl || '';
      },
      positionResolveLabel() { return this.hasActivePosition ? 'Re-resolve 3D Position' : 'Resolve 3D Position'; },
      positionResolveColor() { return this.hasActivePosition ? 'success' : 'warning'; },
      positionSaving() { return this.positionFlow.status === 'saving'; },
      positionInteractionLocked() { return ['resolving', 'saving'].includes(this.positionFlow.status); },
      canResolvePosition() {
        return this.pageActive
          && this.viewerOpened
          && this.activeFinding?.approvalStatus === APPROVAL.ACCEPTED
          && this.isPrimaryFindingScene
          && this.hasPanoramaEvidence
          && ['idle', 'saved'].includes(this.positionFlow.status)
          && !this.busy
          && !this.loading;
      },
      fallbackVisible() { return this.polygonInspects.some(row => row.isActive && parseMaskPoints(row.maskPoints).length < 3); },
      planDisabledReason() { return !this.selectedRows.length ? 'Select anomalies on this page' : this.selectedRows.some(row => !hasSpatialPoint(row.point3d)) ? 'Selected anomalies need a 3D point before they can be added to a plan' : ''; },
      canIsolateSelection() { return this.selectedRows.length > 0 && this.selectedRows.every(this.canIsolate); },
      filterChips() { return Object.entries(this.options.filters || {}).filter(([key]) => key !== 'approvalStatus').flatMap(([key, values]) => (Array.isArray(values) ? values : []).map(value => ({ key, value, label: getFilterOptionDisplayName(key) || key }))); },
      filtersApplied() { return this.filterChips.length > 0 || !!this.options.search; },
      pickerFinding() {
        const row = this.activeFinding?.id === this.componentPicker.findingId
          ? this.activeFinding
          : this.entries.find(entry => entry.id === this.componentPicker.findingId);
        return row
          ? {
              ...row,
              badgeIndex: row.friendlyId || row.id,
              scanName: this.scanName(row.imageId) || row.scanName || row.imageName || '',
            }
          : null;
      },
    },
    created() {
      this._positionCloudLease = null;
      this._positionModelLease = null;
      this._positionPreviewHandle = null;
      this._releasedPositionResources = new WeakSet();
      this.detailLoader = createAnomalyDetailLoader({
        load: id => aIInspectService.getById(id, { facilityId: this.facilityId, 'Filters.ApprovalStatus': ['Accepted'] }).then(response => response.data),
        busy: value => { this.detailLoading = value; },
        apply: detail => { this.activeDetail = detail; this.focusCurrentPolygon(); },
        failed: err => { this.detailError = err.message || 'Could not load anomaly details.'; this.activeDetail = null; },
      });
    },
    mounted() { this.enterPage(); },
    activated() { this.enterPage(); },
    deactivated() { this.leavePage(); },
    beforeDestroy() { this.leavePage(); },
    watch: {
      facilityId() { if (this.pageActive) { this.leavePage(); this.enterPage(); } },
      options: { deep: true, handler() { if (!this.pageActive || JSON.stringify(this.options) === this.querySignature) return; this.clearSelectionAndContext(); this.queryPending = true; clearTimeout(this.requestTimer); this.requestTimer = setTimeout(() => this.refresh(), 300); } },
      viewerTab() { if (this.pageActive && this.viewerOpened) this.handleViewerTabChange(); },
    },
    methods: {
      enterPage() {
        if (this.pageActive) return;
        if (this.recentComponentsFacilityId !== this.facilityId) {
          this.recentComponents = [];
          this.recentComponentsFacilityId = this.facilityId;
        }
        this.$store.commit('AI_INSPECT/SET_FACILITY_ID', this.facilityId);
        this.pageActive = true;
        this.refresh();
        this.loadImages();
      },
      leavePage() {
        this.pageActive = false;
        this.invalidateFindingPositionFlow({ force: true });
        this.queryPending = false;
        this.scopeVersion += 1;
        clearTimeout(this.requestTimer);
        this.detailLoader?.invalidate();
        this.$store.commit('AI_INSPECT/INVALIDATE_REQUESTS');
        this.clearSelectionAndContext();
        this.$refs.filters?.closeUpdateSlideout();
        this.componentPicker = { open: false, findingId: null };
      },
      async refresh() {
        if (!this.pageActive || this.positionInteractionLocked) return;
        clearTimeout(this.requestTimer);
        this.queryPending = false;
        this.querySignature = JSON.stringify(this.options);
        const scope = this.scopeVersion;
        try {
          const page = await this.$store.dispatch('AI_INSPECT/GET');
          if (!page || !this.pageActive || scope !== this.scopeVersion) return;
          if (!page.items.length && page.total > 0 && this.options.page > 1) { this.setOptions({ page: Math.max(1, Math.ceil(page.total / this.options.itemsPerPage)) }); return; }
          const ids = new Set(this.entries.map(row => row.id));
          this.selectedIds = this.selectedIds.filter(id => ids.has(id));
          if (this.activeId && !ids.has(this.activeId)) this.closeDetails();
          else if (this.activeId) { this.activeDetail = null; this.detailLoader.open(this.activeFinding); }
        } catch (err) { /* The store exposes initial/refresh failure through LAST_ERROR. */ }
      },
      toggleGroup(avaInspectId) { if (!this.positionInteractionLocked) this.$store.dispatch('AI_INSPECT/TOGGLE_GROUP', avaInspectId); },
      retryGroup(avaInspectId) { this.$store.dispatch('AI_INSPECT/LOAD_GROUP', avaInspectId); },
      async loadImages() { try { await this.$store.dispatch('AI_INSPECT/GET_IMAGES'); this.focusCurrentPolygon(); } catch (err) { /* IMAGES_ERROR exposes a compact viewer retry action. */ } },
      reportSpatialError(message) {
        this.spatialError = message;
        this.$dialog.notify.warning(message);
      },
      setOptions(value) { if (!this.positionInteractionLocked) this.$store.commit('AI_INSPECT/SET_OPTIONS', { ...this.options, ...value }); },
      search(value) { this.setOptions({ search: value || '', page: 1 }); },
      clearFilters() { this.setOptions({ page: 1, search: '', filters: Object.fromEntries(Object.keys(this.options.filters || {}).map(key => [key, []])) }); },
      removeFilter(chip) { this.setOptions({ page: 1, filters: { ...this.options.filters, [chip.key]: this.options.filters[chip.key].filter(value => value !== chip.value) } }); },
      setDensity(value) { this.density = value; storeDensity(DENSITY_KEY, value); },
      scanName(id) { return this.images.find(image => image.id === id)?.name || ''; },
      clearSelectionAndContext() { this.selectedIds = []; this.closeDetails(); },
      closeDetails() { if (this.positionSaving) return; this.detailLoader?.invalidate(); this.activeId = null; this.activeDetail = null; this.detailError = null; this.closeViewer(); },
      closeViewer() {
        if (this.positionSaving) return;
        this.invalidateFindingPositionFlow();
        this.viewerVersion += 1; this.detailLoader?.invalidate(); this.viewerOpened = false; this.viewerExpanded = false; this.viewerImageId = null; this.isolatedInspects = []; this.spatialError = null;
      },
      openAnomaly(row) {
        if (!row || row.approvalStatus !== APPROVAL.ACCEPTED || this.busy || this.positionSaving) return;
        this.invalidateFindingPositionFlow();
        this.scopeVersion += 1;
        this.viewerVersion += 1;
        this.activeId = row.id; this.activeDetail = null; this.detailError = null; this.spatialError = null;
        this.isolatedInspects = [];
        this.viewerOpened = true; this.viewerImageId = row.imageId; this.viewerTab = '360 View';
        this.focusCurrentPolygon();
        return this.detailLoader.open(row);
      },
      focusCurrentPolygon() {
        this.$nextTick(() => {
          if (!this.pageActive || !this.viewerOpened || this.viewerTab !== '360 View') return;
          const inspect = this.polygonInspects.find(row => row.ownerId === this.activeId);
          if (inspect) this.$refs.viewersSection?.focusInspect(inspect);
        });
      },
      showScene(scene) {
        if (!scene || this.positionSaving) return;
        if (this.viewerImageId !== scene.imageId) this.invalidateFindingPositionFlow();
        this.viewerImageId = scene.imageId;
        this.focusCurrentPolygon();
      },
      onPolygonSelected(inspect) {
        const row = this.entries.find(item => item.id === (inspect.ownerId || inspect.id));
        if (!row) return;
        if (row.id !== this.activeId) this.openAnomaly(row);
        this.$nextTick(() => { const element = [...this.$el.querySelectorAll('tr[data-id]')].find(el => el.dataset.id === row.id); element?.scrollIntoView({ block: 'nearest' }); });
      },
      openAnalysis() { this.$router.push({ name: 'ai-analysis', params: { id: this.facilityId } }); },
      openSourceReport(row) { if (row?.analysisJobId) this.$router.push({ name: 'ai-analysis-job', params: { id: this.facilityId, jobId: row.analysisJobId } }); },
      openComponentPicker(row) {
        if (!row || this.busy || row.approvalStatus !== APPROVAL.ACCEPTED) return;
        this.componentPicker = { open: true, findingId: row.id };
      },
      componentLinkError(err) { return componentLinkErrorMessage(err); },
      async updateComponentLink(row, component) {
        if (!row || this.linkingComponent) return false;
        const scope = this.scopeVersion;
        const patch = componentPatch(component);
        this.linkingComponent = true;
        this.busy = true;
        try {
          const updated = await this.$store.dispatch('AI_INSPECT/LINK_COMPONENT', { id: row.id, masterTagId: patch.masterTagId });
          if (!updated || !this.pageActive || scope !== this.scopeVersion) return false;
          if (this.activeDetail?.id === updated.id) this.activeDetail = { ...this.activeDetail, ...updated };
          if (component) this.recentComponents = pushRecent(this.recentComponents, component);
          this.$dialog.notify.success(component ? `Linked ${row.friendlyId || row.id} to ${component.name}` : `Removed the component link from ${row.friendlyId || row.id}`);
          return true;
        } catch (err) {
          if (this.pageActive && scope === this.scopeVersion) {
            this.$dialog.notify.error(this.componentLinkError(err));
            if (err?.response?.status === 409) {
              this.componentPicker.open = false;
              await this.refresh();
            }
          }
          return false;
        } finally {
          this.linkingComponent = false;
          this.busy = false;
        }
      },
      async linkComponent(component) {
        const row = this.pickerFinding;
        if (!row || !component) return;
        const scope = this.scopeVersion, facilityId = this.facilityId;
        const current = componentOfFinding(row);
        if (current?.id === component.id) { this.componentPicker.open = false; return; }
        if (current && !(await this.$dialog.confirm({ title: 'Replace component link', text: `Replace ${current.name} with ${component.name}?`, actions: { false: { text: 'Keep', color: 'secondary' }, true: { text: 'Replace', color: 'primary' } } }))) return;
        if (!this.pageActive || scope !== this.scopeVersion || facilityId !== this.facilityId) return;
        if (await this.updateComponentLink(row, component)) this.componentPicker.open = false;
      },
      async unlinkComponent(row) {
        const scope = this.scopeVersion, facilityId = this.facilityId;
        const current = componentOfFinding(row);
        if (!current || !(await this.$dialog.confirm({ title: 'Remove component link', text: `Remove the link between ${row.friendlyId || row.id} and ${current.name}?`, actions: { false: { text: 'Keep', color: 'secondary' }, true: { text: 'Remove link', color: 'error' } } }))) return false;
        if (!this.pageActive || scope !== this.scopeVersion || facilityId !== this.facilityId) return false;
        return this.updateComponentLink(row, null);
      },
      async unlinkPickerComponent() {
        if (await this.unlinkComponent(this.pickerFinding)) this.componentPicker.open = false;
      },
      async saveDetails(draft) {
        if (this.busy || this.detailLoading || this.detailError || !this.activeFinding || draft.id !== this.activeId) return;
        const scope = this.scopeVersion;
        this.busy = true;
        try {
          await this.$store.dispatch('AI_INSPECT/UPDATE', { ...draft, approvalStatus: APPROVAL.ACCEPTED });
          if (!this.pageActive || scope !== this.scopeVersion) return;
          await this.detailLoader.open(draft);
          if (!this.detailError) this.$dialog.notify.success('Anomaly details saved');
        } catch (err) { if (this.pageActive && scope === this.scopeVersion) this.$dialog.notify.error('Could not save anomaly details. Your draft is still available.'); }
        finally { this.busy = false; }
      },
      async openInspectReport(row) {
        if (!row || this.busy) return;
        const scope = this.scopeVersion;
        this.busy = true;
        try {
          const url = row.reportUrl || (await this.$store.dispatch('AI_INSPECT/GENERATE_REPORT', { id: row.id }))?.reportUrl;
          if (!url) throw new Error('Report unavailable');
          if (this.pageActive && scope === this.scopeVersion) this.$refs.inspectReport.openDialog({ url });
        } catch (err) { if (this.pageActive && scope === this.scopeVersion) this.$dialog.notify.error('Could not open the anomaly report'); }
        finally { this.busy = false; }
      },
      deleteInspect(row) { this.confirmDelete([row]); },
      deleteSelected() { this.confirmDelete(this.selectedRows); },
      async confirmDelete(rows) {
        if (!rows.length || this.busy || this.positionInteractionLocked) return;
        const scope = this.scopeVersion;
        const accepted = await this.$dialog.confirm({ title: 'Delete verified anomalies', text: `Delete ${rows.length} verified ${rows.length === 1 ? 'anomaly' : 'anomalies'}? Deleting evidence does not mark a defect as solved.`, actions: { false: 'Cancel', true: { text: 'Delete', color: 'error' } } });
        if (!accepted || !this.pageActive || scope !== this.scopeVersion) return;
        this.busy = true;
        try {
          if (rows.length === 1) await aIInspectService.delete(rows[0].id);
          else await aIInspectService.deleteMultiple(rows.map(row => row.id));
          if (this.pageActive && scope === this.scopeVersion) { this.clearSelectionAndContext(); await this.refresh(); }
        } catch (err) { if (this.pageActive) this.$dialog.notify.error('Could not delete the selected anomalies'); }
        finally { this.busy = false; }
      },
      addToPlan() { if (!this.planDisabledReason && !this.busy && !this.positionInteractionLocked) this.$refs.addToPlan.open(this.selectedRows); },
      async assessCondition() {
        if (!this.selectedRows.length || this.busy || this.positionInteractionLocked) return;
        const ids = this.selectedRows.map(row => row.id), facilityId = this.facilityId, scope = this.scopeVersion;
        this.busy = true;
        try {
          await this.$refs.conditionAssessmentModal.open(ids.length, facilityId);
          if (!this.pageActive || scope !== this.scopeVersion) { this.$refs.conditionAssessmentModal.close(); return; }
          await coatingAnalyzerService.add({ anomalyInsightIds: ids, facilityId });
          if (this.pageActive && scope === this.scopeVersion) this.$refs.conditionAssessmentModal.setCompleted();
        } catch (err) {
          if (err !== false && this.pageActive && scope === this.scopeVersion) { error('Error adding verified anomalies to coating analysis:', err); this.$refs.conditionAssessmentModal.close(); this.$dialog.notify.error('Failed to add items to coating analysis'); }
        } finally { this.busy = false; }
      },
      async releaseFindingPositionResources({
        lease = this._positionCloudLease,
        modelLease = this._positionModelLease,
        preview = this._positionPreviewHandle,
        revision = this.positionFlow.revision,
      } = {}) {
        let cleanupFailed = false;
        if (!this._releasedPositionResources) this._releasedPositionResources = new WeakSet();
        if (this._positionPreviewHandle === preview) this._positionPreviewHandle = null;
        if (this._positionCloudLease === lease) this._positionCloudLease = null;
        if (this._positionModelLease === modelLease) this._positionModelLease = null;
        if (preview && !this._releasedPositionResources.has(preview)) {
          this._releasedPositionResources.add(preview);
          try {
            preview.clear();
          } catch (err) {
            cleanupFailed = true;
            this.reportPositionCleanupWarning(err, revision);
          }
        }
        if (lease && !this._releasedPositionResources.has(lease)) {
          this._releasedPositionResources.add(lease);
          try {
            const cleanupError = await lease.release();
            if (cleanupError) {
              cleanupFailed = true;
              this.reportPositionCleanupWarning(cleanupError, revision);
            }
          } catch (err) {
            cleanupFailed = true;
            this.reportPositionCleanupWarning(err, revision);
          }
        }
        if (modelLease && !this._releasedPositionResources.has(modelLease)) {
          this._releasedPositionResources.add(modelLease);
          try {
            const cleanupError = await modelLease.release();
            if (cleanupError) {
              cleanupFailed = true;
              this.reportPositionCleanupWarning(cleanupError, revision);
            }
          } catch (err) {
            cleanupFailed = true;
            this.reportPositionCleanupWarning(err, revision);
          }
        }
        return !cleanupFailed;
      },
      invalidateFindingPositionFlow({ force = false } = {}) {
        if (this.positionSaving && !force) return Promise.resolve(false);
        const revision = this.positionFlow.revision + 1;
        const lease = this._positionCloudLease;
        const modelLease = this._positionModelLease;
        const preview = this._positionPreviewHandle;
        this.positionFlow = idlePositionFlow(revision);
        return this.releaseFindingPositionResources({ lease, modelLease, preview, revision }).then(() => true);
      },
      reportPositionCleanupWarning(err, revision) {
        if (this.pageActive && this.positionFlow.revision === revision) {
          const positionSaved = this.positionFlow.status === 'saved';
          this.positionFlow = {
            ...this.positionFlow,
            status: positionSaved ? 'saved' : 'error',
            errorCode: 'POSITION_CLEANUP_FAILED',
            message: positionSaved
              ? '3D position saved, but the prior model display could not be restored. Close and reopen the viewer.'
              : 'The prior 3D display could not be fully restored. Close and reopen the viewer before retrying.',
          };
          return;
        }
        warn('Could not restore the prior point-cloud display.', err);
      },
      async resolveActiveFindingPosition() {
        const finding = this.activeFinding;
        if (!this.canResolvePosition || !finding || this.viewerImageId !== finding.imageId) return;
        const image = this.images.find(entry => entry.id === finding.imageId);
        const facilityId = this.facilityId;
        const expectedUpdateDate = finding.updateDate;
        const revision = this.positionFlow.revision + 1;
        this.positionFlow = { ...idlePositionFlow(revision), status: 'resolving', findingId: finding.id };
        let localLease = null;
        let localModelLease = null;
        let localPreview = null;
        let transferredToView = false;
        const isCurrent = () => this.pageActive
          && this.activeId === finding.id
          && this.facilityId === facilityId
          && this.viewerImageId === finding.imageId
          && this.positionFlow.revision === revision;
        try {
          const cleanupSucceeded = await this.releaseFindingPositionResources({ revision });
          if (!cleanupSucceeded || !isCurrent()) return;
          this.viewerTab = '3D View';
          await this.$nextTick();
          if (!isCurrent()) return;
          const viewer = await this.$refs.viewersSection.load3dViewer({
            pointCloudOnly: !this.$store.state.model3dAvailable,
            propagateErrors: true,
          });
          if (!isCurrent()) return;
          localModelLease = await acquireFindingModelVisibility({
            viewer,
            onCleanupError: cleanupErr => this.reportPositionCleanupWarning(cleanupErr, revision),
          });
          this._positionModelLease = localModelLease;
          if (!isCurrent()) return;
          localLease = await acquireFindingPointCloud({
            image,
            pointClouds: this.$store.state.pointClouds,
            forgeService: this.forgeService,
            onCleanupError: cleanupErr => this.reportPositionCleanupWarning(cleanupErr, revision),
          });
          this._positionCloudLease = localLease;
          if (!isCurrent()) return;
          await localModelLease.rehide();
          if (!isCurrent()) return;
          const resolvedCandidate = await resolveFindingPosition({
            finding,
            image,
            ensureViewer: async () => viewer,
            ensurePointCloud: async () => localLease,
            forgeService: this.forgeService,
            pixelToYawPitch,
            isCurrent,
          });
          if (!isCurrent()) return;
          const candidate = { ...resolvedCandidate, expectedUpdateDate };
          localPreview = showFindingPositionPreview(viewer, candidate.point3d);
          this.forgeService.zoomToPoint3dViewer(new THREE.Vector3(
            candidate.point3d.x,
            candidate.point3d.y,
            candidate.point3d.z,
          ));
          if (!isCurrent()) return;
          this._positionPreviewHandle = localPreview;
          transferredToView = true;
          this.positionFlow = { ...this.positionFlow, status: 'preview', candidate };
          this.$nextTick(() => this.$refs.positionPreview?.focus());
        } catch (err) {
          if (isCurrent() && err.code !== 'POSITION_REQUEST_STALE') {
            this.positionFlow = {
              ...this.positionFlow,
              status: 'error',
              errorCode: err.code || 'POSITION_RESOLUTION_FAILED',
              message: err.message || 'The finding position could not be resolved.',
            };
            this.isolatedInspects = [];
            this.viewerImageId = finding.imageId;
            this.viewerTab = '360 View';
            this.focusCurrentPolygon();
            this.$nextTick(() => this.$refs.retryPositionButton?.$el?.focus());
          }
        } finally {
          if (!transferredToView) {
            await this.releaseFindingPositionResources({
              lease: localLease,
              modelLease: localModelLease,
              preview: localPreview,
              revision,
            });
          }
        }
      },
      async confirmFindingPosition() {
        const finding = this.activeFinding;
        const candidate = this.positionFlow.candidate;
        const revision = this.positionFlow.revision;
        const facilityId = this.facilityId;
        const viewerTab = this.viewerTab;
        const viewerVersion = this.viewerVersion;
        if (!finding || !candidate || this.positionFlow.status !== 'preview') return;
        this.positionFlow = { ...this.positionFlow, status: 'saving' };
        const ownedLease = this._positionCloudLease;
        const ownedModelLease = this._positionModelLease;
        const ownedPreview = this._positionPreviewHandle;
        const isCurrentSave = () => this.pageActive
          && this.facilityId === facilityId
          && this.activeId === finding.id
          && this.positionFlow.revision === revision
          && this.viewerTab === viewerTab
          && this.viewerVersion === viewerVersion;
        try {
          const updated = await this.$store.dispatch('AI_INSPECT/UPDATE_POSITION', {
            id: finding.id,
            imageId: candidate.imageId,
            point3d: candidate.point3d,
            expectedUpdateDate: candidate.expectedUpdateDate,
          });
          if (!updated) {
            if (isCurrentSave()) {
              this.positionFlow = {
                ...this.positionFlow,
                status: 'error',
                errorCode: 'ANOMALY_INSIGHT_POSITION_CONFLICT',
                message: 'The finding changed while the position was being reviewed. Refresh and resolve again.',
              };
              await this.releaseFindingPositionResources({
                lease: ownedLease,
                modelLease: ownedModelLease,
                preview: ownedPreview,
                revision,
              });
              if (!isCurrentSave()) return;
              this.isolatedInspects = [];
              this.viewerImageId = finding.imageId;
              this.viewerTab = '360 View';
              this.focusCurrentPolygon();
            }
            return;
          }
          if (!isCurrentSave()) return;
          this.activeDetail = { ...(this.activeDetail || finding), ...updated };
          this.positionFlow = { ...this.positionFlow, status: 'saved' };
          const modelRestored = await this.releaseFindingPositionResources({
            lease: null,
            modelLease: ownedModelLease,
            preview: null,
            revision,
          });
          if (!isCurrentSave()) return;
          if (!modelRestored) {
            this.positionFlow = {
              ...this.positionFlow,
              status: 'saved',
              errorCode: 'POSITION_CLEANUP_FAILED',
              message: '3D position saved, but the prior model display could not be restored. Close and reopen the viewer.',
            };
            this.$dialog.notify.warning(this.positionFlow.message);
          } else {
            this.$dialog.notify.success('3D position saved');
          }
        } catch (err) {
          if (isCurrentSave()) {
            this.positionFlow = mapPositionSaveError(this.positionFlow, err);
            await this.releaseFindingPositionResources({
              lease: ownedLease,
              modelLease: ownedModelLease,
              preview: ownedPreview,
              revision,
            });
            if (!isCurrentSave()) return;
            this.isolatedInspects = [];
            this.viewerImageId = finding.imageId;
            this.viewerTab = '360 View';
            this.focusCurrentPolygon();
            this.$nextTick(() => {
              if (isCurrentSave()) this.$refs.retryPositionButton?.$el?.focus();
            });
          }
        } finally {
          if (!isCurrentSave()) {
            await this.releaseFindingPositionResources({
              lease: ownedLease,
              modelLease: ownedModelLease,
              preview: ownedPreview,
              revision,
            });
          }
        }
      },
      async returnToFindingEvidence() {
        const finding = this.activeFinding;
        if (!finding || this.positionSaving) return;
        const facilityId = this.facilityId;
        const revision = this.positionFlow.revision + 1;
        const viewerTab = this.viewerTab;
        const viewerVersion = this.viewerVersion;
        const invalidated = await this.invalidateFindingPositionFlow();
        const isCurrent = () => invalidated
          && this.pageActive
          && this.facilityId === facilityId
          && this.activeId === finding.id
          && this.positionFlow.revision === revision
          && this.viewerTab === viewerTab
          && this.viewerVersion === viewerVersion;
        if (!isCurrent()) return;
        this.isolatedInspects = [];
        this.viewerOpened = true;
        this.viewerImageId = finding.imageId;
        this.viewerTab = '360 View';
        this.focusCurrentPolygon();
        this.$nextTick(() => {
          if (isCurrent()) this.$refs.resolvePositionButton?.$el?.focus();
        });
      },
      retryFindingPosition() { return this.returnToFindingEvidence(); },
      cancelFindingPosition() {
        if (this.positionFlow.status !== 'error') return this.returnToFindingEvidence();
        const finding = this.activeFinding;
        if (!finding || this.positionSaving) return Promise.resolve(false);
        const revision = this.positionFlow.revision + 1;
        const cleanup = this.invalidateFindingPositionFlow();
        this.isolatedInspects = [];
        this.viewerOpened = true;
        this.viewerImageId = finding.imageId;
        this.viewerTab = '360 View';
        this.focusCurrentPolygon();
        this.$nextTick(() => {
          if (this.pageActive && this.positionFlow.revision === revision) {
            this.$refs.resolvePositionButton?.$el?.focus();
          }
        });
        return cleanup;
      },
      canIsolate(row) { return hasSpatialPoint(row?.point3d) || !!row?.dbIds?.length || !!row?.inspection?.children?.length || !!(row?.masterTagId && TAG_SERVICES[componentTypeOf(row.masterTagType)]); },
      isolateIn3D(row) { if (!row || this.busy) return; this.isolatedInspects = [row]; this.viewerOpened = true; this.spatialError = null; if (this.viewerTab === '3D View') this.handleViewerTabChange(); else this.viewerTab = '3D View'; },
      isolateSelected() { if (!this.canIsolateSelection || this.positionInteractionLocked) return; this.isolatedInspects = this.selectedRows; this.viewerOpened = true; this.spatialError = null; if (this.viewerTab === '3D View') this.handleViewerTabChange(); else this.viewerTab = '3D View'; },
      async handleViewerTabChange() {
        await this.$nextTick();
        if (!this.viewerOpened) return;
        if (this.positionSaving) {
          if (this.viewerTab !== '3D View') this.viewerTab = '3D View';
          return;
        }
        const positionOwns3d = this.viewerTab === '3D View'
          && ['resolving', 'preview', 'saving', 'saved'].includes(this.positionFlow.status);
        if (positionOwns3d) return;
        if (['resolving', 'preview', 'saved'].includes(this.positionFlow.status)) {
          await this.invalidateFindingPositionFlow();
        }
        const viewerScope = ++this.viewerVersion;
        this.spatialError = null;
        if (this.viewerTab === '360 View') { this.focusCurrentPolygon(); return; }
        const scope = this.scopeVersion, tab = this.viewerTab;
        const rows = this.isolatedInspects.length ? this.isolatedInspects : this.activeFinding ? [this.activeFinding] : [];
        if (!rows.length) { this.reportSpatialError('Select an anomaly to show its spatial context.'); return; }
        try {
          await this.$refs.viewersSection?.load3dViewer({ propagateErrors: true });
          if (!this.pageActive || !this.viewerOpened || scope !== this.scopeVersion || viewerScope !== this.viewerVersion || this.viewerTab !== tab) return;
          if (tab === '2D View') { await this.showIn2D(rows, scope, viewerScope); return; }
          const dbIds = [], points = [];
          for (const row of rows) {
            let children = row.inspection?.children || [];
            if (!children.length && !row.dbIds?.length && row.masterTagId && TAG_SERVICES[componentTypeOf(row.masterTagType)]) {
              const { data } = await TAG_SERVICES[componentTypeOf(row.masterTagType)].getById(row.masterTagId);
              if (scope !== this.scopeVersion || viewerScope !== this.viewerVersion || !this.pageActive) return;
              if (data?.id !== row.masterTagId || (data.assetId && data.assetId !== this.facilityId)) throw new Error('Linked component is unavailable in this facility.');
              children = data.children || [];
            }
            dbIds.push(...(row.dbIds || []), ...children.map(child => child.dbId).filter(id => id != null));
            if (hasSpatialPoint(row.point3d)) points.push(new THREE.Vector3(Number(row.point3d.x), Number(row.point3d.y), Number(row.point3d.z)));
          }
          if (!dbIds.length && !points.length) throw new Error('No 3D geometry is available for the selected anomalies.');
          if (!this.viewer) throw new Error('The 3D viewer is unavailable.');
          this.viewer.isolate();
          if (dbIds.length) this.forgeService.getMainExtension()?.isolateDbIds([...new Set(dbIds)]);
          if (points.length === 1) this.forgeService.zoomToPoint3dViewer(points[0]);
          else if (points.length > 1) this.viewer.navigation?.fitBounds(true, new THREE.Box3().setFromPoints(points));
          const inspections = rows.filter(row => hasSpatialPoint(row.inspection?.point)).map(row => { const inspection = Object.assign(new ForgeInspection(), row.inspection); inspection.parseContent(); return inspection; });
          if (inspections.length) this.forgeService.getLabels3DExtension()?.createHighlightingTags(inspections);
          if (points.length !== rows.length) this.reportSpatialError('Showing component context where an exact anomaly point is unavailable.');
        } catch (err) { if (this.pageActive && scope === this.scopeVersion && viewerScope === this.viewerVersion) this.reportSpatialError(err.message || 'Could not show spatial context.'); }
      },
      async showIn2D(rows, scope, viewerScope) {
        const points = rows.map(row => hasSpatialPoint(row.inspection?.point) ? row.inspection.point : hasSpatialPoint(row.point3d) ? this.forgeService.view3dToWorld(row.point3d) : null);
        if (points.some(point => !point)) throw new Error('Every selected anomaly needs a spatial point for the 2D plan.');
        const levels = [...new Set(points.map(point => this.forgeService.getLevelContainsPoint(point)))];
        if (levels.length !== 1 || !levels[0]?.planUrn) throw new Error('Select anomalies on one level with an available 2D plan.');
        this.$store.commit('setSelectedLevel', levels[0]);
        await this.$refs.viewersSection.load2dViewer(levels[0].planUrn);
        if (scope !== this.scopeVersion || viewerScope !== this.viewerVersion || !this.pageActive) return;
        this.forgeService.zoomToPoints2D(points);
        const inspections = rows.map((row, index) => Object.assign(new ForgeInspection(), row.inspection || {}, { point: points[index] }));
        this.forgeService.getLabels2DExtension()?.createHighlightingTags(inspections);
      },
      async openCorrosionAnalyser(item) {
        // Validate point3d exists
        if (!hasSpatialPoint(item.point3d)) {
          this.$dialog.notify.error('No 3D point available for this inspection.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        // Find the image associated with this anomaly insight
        const selectedImage = this.images.find((img) => img.id === item.imageId);
        if (!selectedImage) {
          this.$dialog.notify.error('Image not found for this inspection.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        // Find corresponding point cloud using OPEN_360_CORRESPONDING_PC analyzer priority pattern
        let correspondingPC = null;

        // Priority 1: Parent project point cloud (for consistent analysis)
        const parentProject = this.$store.state.projects?.find((p) =>
          p.images?.some((img) => img.id === selectedImage.id),
        );
        if (parentProject?.projectPointClouds) {
          correspondingPC = parentProject.projectPointClouds;
        }

        // Priority 2: Image's own scan point cloud (fallback)
        if (!correspondingPC) {
          correspondingPC = selectedImage.scanPointClouds;
        }

        // Priority 3: Any opened point cloud (fallback)
        if (!correspondingPC) {
          correspondingPC = this.$store.getters['getPointClouds']?.find(
            (pc) => pc.isShown === true,
          );
        }

        if (!correspondingPC) {
          this.$dialog.notify.error(
            'No point cloud available. Please ensure a point cloud is loaded.',
            {
              position: 'top-right',
              timeout: 3000,
            },
          );
          return;
        }

        if (!correspondingPC.url) {
          this.$dialog.notify.error('Point cloud URL is not available.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        const pointCloud = correspondingPC;

        // 1 m crop box around the finding, converted to the model's unit.
        let cropBox;
        try {
          cropBox = viewerCubeAround(item.point3d, CORROSION_CROP_HALF_SIZE_M);
        } catch (err) {
          this.$dialog.notify.error(err.message, { position: 'top-right', timeout: 4000 });
          return;
        }

        // Default transformation (identity)
        const forgePCInstance = {
          position: new THREE.Vector3(0, 0, 0),
          quaternion: new THREE.Quaternion(),
          scale: new THREE.Vector3(1, 1, 1),
          pcoGeometry: null,
        };

        // Prepare dialog data
        const transformationData = {
          visible: true,
          cropBox: cropBox,
          pointCloudUrl: pointCloud.url,
          pointCloudName: pointCloud.name,
          pointCloudId: pointCloud.id,
          forgePCInstance: forgePCInstance,
          unitScale: this.$store.state.unitScale,
          globalOffset: this.$store.state.globalOffset || new THREE.Vector3(0, 0, 0),
          tagId: null, // No tag yet - new analysis
          analysisResult: null,
        };

        // Open CroppedMeasurementsDialog
        this.$store.commit('SET_CROPPED_MEASUREMENT_DIALOG', transformationData);

        this.$dialog.notify.info('Opening corrosion analyser...', {
          position: 'top-right',
          timeout: 2000,
        });
      },

    },
  };
</script>

<style lang="scss">
  @import '../components/aiAnalysis/aiAnalysisTokens';
  .verified-inspect {
    @include ai-analysis-tokens;
    &.theme-dark { @include ai-analysis-tokens-dark; }
    display: flex; flex-direction: column; gap: var(--ds-space-md, 16px); height: 100%; min-height: 0; padding-top: 16px; box-sizing: border-box; color: var(--aa-text-primary);
    .inspect-toolbar__control { height: 40px !important; min-width: 40px !important; }
    .inspect-filter-button { padding: 0 16px !important; font-size: 14px; }
    .inspect-search { width: 100%; max-width: 310px; }
    .inspect-filters { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; padding: 8px 16px; border-top: 1px solid var(--aa-border-subtle); }
    .inspect-layout { display: grid; grid-template-columns: minmax(0, 1fr); flex: 1 1 auto; gap: 16px; align-items: stretch; min-width: 0; min-height: 0; }
    .inspect-layout--viewer { grid-template-columns: minmax(0, 1.7fr) minmax(350px, 1fr); }
    .inspect-details { display: flex; flex: 0 0 380px; flex-direction: column; width: 380px; height: 100%; min-width: 0; min-height: 0; overflow: hidden; background: var(--aa-surface); border-top: 1px solid var(--aa-border-default); border-left: 1px solid var(--aa-border-default); }
    .inspect-details .verified-anomaly-detail { width: 100%; height: 100%; flex: 1 1 auto; border-left: 0; }
    .inspect-viewer { position: relative; display: flex; flex-direction: column; height: 100%; min-height: 0; border: 1px solid var(--aa-border-default); border-radius: var(--ds-radius-md, 8px); background: var(--aa-surface); overflow: hidden; }
    .inspect-viewer > #viewersSection { flex: 1 1 auto; height: auto !important; min-height: 0; }
    .inspect-viewer__fallback { position: absolute; z-index: 4; right: 12px; bottom: 12px; left: 12px; margin: 0; font-size: 13px; }
    .inspect-viewer__empty { position: absolute; z-index: 3; inset: 52px 0 0; display: grid; place-items: center; padding: 32px; text-align: center; color: var(--aa-text-muted); background: var(--aa-surface); }
    .inspect-viewer__empty p { margin: 4px 0; font-size: 13px; }
    .inspect-viewer__image-retry { position: absolute !important; z-index: 6; top: 64px; right: 16px; width: 40px !important; height: 40px !important; min-width: 40px !important; box-shadow: 0 3px 10px rgba(0, 0, 0, .28); }
    .inspect-viewer--expanded { position: fixed; inset: 24px; width: auto; height: auto; min-height: 0; z-index: 200; box-shadow: 0 0 0 100vmax rgba(0, 0, 0, .4); }
    .inspect-scenes { display: flex; overflow: auto; gap: 8px; padding: 8px; flex: 0 0 auto; border-top: 1px solid var(--aa-border-subtle); }
    .inspect-scenes button { display: flex; align-items: center; gap: 6px; flex: 0 0 auto; max-width: 180px; padding: 4px; border: 1px solid var(--aa-border-default); border-radius: 4px; }
    .inspect-scenes button.is-current { border-color: var(--aa-brand); background: var(--aa-brand-soft); }
    .inspect-scenes img { width: 40px; height: 36px; object-fit: cover; }
    .inspect-scenes span { font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .finding-position-action { position: absolute !important; z-index: 6; top: 16px; right: 16px; width: 40px !important; height: 40px !important; min-width: 40px !important; box-shadow: 0 3px 10px rgba(0, 0, 0, .28); }
    .finding-position-resolving-cover { position: absolute; z-index: 7; inset: 0; display: grid; overflow: hidden; place-items: center; background: var(--aa-surface); }
    .finding-position-resolving-cover__image { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
    .finding-position-resolving-cover::after { position: absolute; inset: 0; background: rgba(12, 18, 24, .72); content: ''; }
    .finding-position-resolving-cover__content { position: relative; z-index: 1; display: flex; align-items: center; gap: 10px; padding: 10px 14px; color: #fff; background: rgba(12, 18, 24, .86); border-radius: var(--ds-radius-md, 8px); }
    // Position status banner: docked viewer chrome, solid so it reads on any photo.
    // In the 360 view it sits on top of the Photo Sphere Viewer navbar (40px).
    .finding-position-banner {
      --banner-bg: #061a1a;
      --banner-accent: var(--v-secondary-base, #2cc295);
      position: absolute; z-index: 5; right: 0; bottom: 0; left: 0;
      display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 4px 12px; padding: 12px 14px;
      color: #fff; background: var(--banner-bg); box-shadow: 0 -8px 24px rgba(0, 0, 0, .28);
      animation: finding-position-banner-rise .22s cubic-bezier(.16, 1, .3, 1) both;
    }
    .finding-position-banner::before { position: absolute; inset: 0 0 auto; height: 1px; background: rgba(255, 255, 255, .08); content: ''; }
    .finding-position-banner--above-navbar { bottom: 40px; }
    .finding-position-banner:focus-visible, .finding-position-banner :focus-visible { outline: 2px solid var(--banner-accent); outline-offset: 2px; }
    .finding-position-banner__icon { align-self: start; margin-top: 1px; }
    .finding-position-banner__message { margin: 0; font-size: 13px; font-weight: 500; line-height: 1.45; overflow-wrap: anywhere; }
    .finding-position-banner__actions { display: flex; flex-wrap: wrap; grid-column: 2; gap: 8px; margin-top: 8px; }
    .finding-position-banner .v-btn.v-btn--has-bg.finding-position-banner__btn--primary { color: #03241c; background-color: var(--banner-accent); }
    .finding-position-banner .v-btn.v-btn--has-bg.finding-position-banner__btn--outline { color: #fff; background-color: transparent; box-shadow: inset 0 0 0 1px rgba(255, 255, 255, .34); }
    .finding-position-banner .v-btn.finding-position-banner__btn--text { color: rgba(255, 255, 255, .86); }
    @keyframes finding-position-banner-rise { from { opacity: 0; transform: translateY(8px); } }
    @media (prefers-reduced-motion: reduce) { .finding-position-banner { animation: none; } }
    .inspect-scenes button:disabled { opacity: .5; cursor: not-allowed; }
    button:focus-visible { outline: 2px solid var(--aa-focus); outline-offset: 2px; }
    @media (max-width: 1500px) {
      .inspect-viewer #viewersSection .viewer-header__top { gap: 6px; }
      .inspect-viewer #viewersSection .viewer-header__title { min-width: 30px; }
      .inspect-viewer #viewersSection .viewer-header__title-text { display: none; }
      .inspect-viewer #viewersSection .viewer-header__controls { flex: 0 0 auto; margin-left: 0; }
    }
    @media (max-width: 1100px) {
      .inspect-layout--viewer { display: flex; flex-direction: column; overflow-y: auto; }
      .inspect-layout--viewer > .verified-anomalies-table { flex: 0 0 max(620px, calc(100vh - 180px)); height: max(620px, calc(100vh - 180px)); }
      .inspect-viewer { flex: 0 0 520px; height: 520px; }
      .inspect-viewer--expanded { height: auto; }
    }
    @media (max-width: 1000px) {
      .verified-body--with-panel { display: block; overflow-y: auto; }
      .verified-body--with-panel > .table-wrap { height: 230px; min-height: 230px; }
      .inspect-details { flex: none; width: 100%; height: 420px; min-height: 420px; border-top: 1px solid var(--aa-border-default); border-left: 0; }
    }
    @media (max-width: 600px) {
      .inspect-search { flex: 1 1 100%; order: 2; }
      .inspect-viewer--expanded { inset: 8px; }
      .inspect-viewer #viewersSection .viewer-header { padding-right: 6px; padding-left: 6px; }
      .inspect-viewer #viewersSection .viewer-header__title { display: none; }
      .inspect-viewer #viewersSection .viewer-header__tabs { flex: 1 1 auto; min-width: 0; margin-left: 0; }
      .inspect-viewer #viewersSection .viewer-tab-btn { flex: 1 1 0; min-width: 0; padding-right: 4px; padding-left: 4px; justify-content: center; }    }
  }
</style>
